import type { Theme } from '@material-ui/core'
import { Chip, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import React from 'react'
import type { QuoteTag } from 'paintscout'
import { TAG_COLORS } from '@paintscout/util'
import { getContrastRatio, darken } from '@material-ui/core/styles/colorManipulator'
import { Typography } from '../..'
import type { StyleClasses } from '../../styles'

const useStyles = makeStyles<Theme, QuoteTagProps & { tagBackground: string }>(
  (theme) => ({
    root: ({ tag, handleClick, tagBackground }) => {
      const baseColor = tag.color
      let fallbackBackground = `${baseColor}25`

      if (getContrastRatio(baseColor, '#F7F8FA') < 1.25) {
        fallbackBackground = darken(baseColor, 0.5)
      }

      return {
        border: `1px solid ${tag.color}`,
        color: tag.color,
        backgroundColor: tagBackground ? `${tagBackground} !important` : `${fallbackBackground} !important`,
        height: 'fit-content',
        maxWidth: 'inherit',
        cursor: handleClick ? 'pointer' : 'inherit',
        borderRadius: theme.borderRadius.md,
        '&:hover, &:focus, &:active': {
          backgroundColor: tagBackground ? `${tagBackground} !important` : `${fallbackBackground} !important`
        }
      }
    },
    label: {
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
      textOverflow: 'clip',
      padding: theme.spacing(0, 1)
    },
    icon: ({ tag }) => ({
      color: tag.color,
      '&:hover': {
        color: `${tag.color}90`
      },
      '&$icon': {
        width: 16,
        height: 16,
        margin: '0 4px 0 -3px'
      }
    })
  }),
  { name: 'QuoteTag' }
)

interface QuoteTagProps {
  tag: QuoteTag
  handleDelete?: (tag: QuoteTag) => void
  handleClick?: (tag: QuoteTag) => void
  classes?: StyleClasses<typeof useStyles>
}

const QuoteTagChip = (props: QuoteTagProps) => {
  const tagBackground = TAG_COLORS.find((pair) => pair[0] === props.tag.color)?.[1]
  const classes = useStyles({ ...props, tagBackground })
  const { tag, handleDelete, handleClick } = props

  const onClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, tag: QuoteTag) => {
    e.stopPropagation()
    handleClick(tag)
  }

  return (
    <Chip
      classes={{
        root: classes.root,
        label: classes.label
      }}
      label={
        <Typography variant="caption">
          <strong>{tag.label}</strong>
        </Typography>
      }
      onDelete={handleDelete ? () => handleDelete(tag) : undefined}
      onClick={handleClick ? (e) => onClick(e, tag) : undefined}
      deleteIcon={<CloseIcon fontSize="small" className={classes.icon} />}
      variant="outlined"
    />
  )
}

export default QuoteTagChip
